import React, { useEffect, useState } from 'react';
import { LightbulbIcon, MicIcon, MicOffIcon } from 'lucide-react';
import { db } from '../../../utils/db';
import { CO2_ADVICE, co2Band, nl } from '../../../utils/home';
import type { Room } from '../../../types/db';
import type { RoomExtra, SatelliteStatus } from '../../../types/beheer';
const PRESENCE: Record<Room['presence'], {
  label: string;
  dot: string;
}> = {
  beweging: {
    label: 'Beweging',
    dot: 'bg-accent'
  },
  'stil aanwezig': {
    label: 'Stil aanwezig',
    dot: 'bg-accent/60 ring-2 ring-accent/25'
  },
  leeg: {
    label: 'Leeg',
    dot: 'bg-white/20'
  }
};
const SATELLITE: Record<SatelliteStatus, {
  label: string;
  tone: string;
}> = {
  online: {
    label: 'online',
    tone: 'text-ink-soft'
  },
  luistert: {
    label: 'luistert',
    tone: 'text-accent'
  },
  gedempt: {
    label: 'microfoon gedempt',
    tone: 'text-ink-muted'
  },
  offline: {
    label: 'offline',
    tone: 'text-amber'
  }
};
const BAND_TONE = {
  goed: 'text-ink-soft',
  matig: 'text-amber',
  slecht: 'text-alarm'
} as const;
const CO2_MAX = 1600;
interface RoomTileProps {
  room: Room;
  extra?: RoomExtra;
  scene: string | null;
  onScene: (scene: string | null) => void;
}
export function RoomTile({
  room,
  extra,
  scene,
  onScene
}: RoomTileProps) {
  const [light, setLight] = useState(room.light);
  useEffect(() => setLight(room.light), [room.light]);
  const commitLight = () => {
    if (light !== room.light) db.setLight(room.id, light);
  };
  const presence = PRESENCE[room.presence];
  const band = co2Band(room.co2);
  const sat = extra?.satellite;
  const heating = room.target > room.temp + 0.2;
  return <article aria-labelledby={`room-${room.id}`} className="flex flex-col rounded-3xl border border-white/[0.07] bg-night-900 p-5" data-id="element-121" data-mp="2sx0zhj2w2la8">
      <div className="flex items-start justify-between gap-3" data-id="element-122" data-mp="2nxy8h2jqufl9">
        <h3 id={`room-${room.id}`} className="text-[17px] font-semibold text-ink" data-id="element-123" data-mp="3ledhl0w9kwl9">
          {room.name}
        </h3>
        <span className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-soft" title="mmWave-aanwezigheid" data-id="element-124" data-mp="3gm3sfn2z40k1">
          <span className={`h-2 w-2 rounded-full ${presence.dot}`} aria-hidden data-id="element-125" data-mp="1eqeh358pf9vx" />
          {presence.label}
          {extra && <span className="text-ink-muted" data-id="element-126" data-mp="1itavejr7oju2">· {extra.presenceSince}</span>}
        </span>
      </div>

      {/* Climate */}
      <div className="mt-5 flex items-end justify-between gap-4" data-id="element-127" data-mp="19ymnelu94o1k">
        <p className="font-display text-[40px] font-light leading-none tabular-nums text-ink" data-id="element-128" data-mp="19ws1p5b5orwf">
          {nl(room.temp)}
          <span className="ml-0.5 text-[20px] text-ink-muted" data-id="element-129" data-mp="100ozy0k7uva0">°C</span>
        </p>
        <div className="text-right text-[13px] leading-5" data-id="element-130" data-mp="zym1qt8wtvu7">
          <p className={heating ? 'text-amber' : 'text-ink-muted'} data-id="element-131" data-mp="m1z3mn580zmp">
            {heating ? 'Verwarmt naar' : 'Doel'} {nl(room.target)} °C
          </p>
          {extra && <p className="text-ink-muted" data-id="element-132" data-mp="2gf84xrnm5etv">Vocht {extra.humidity}%</p>}
        </div>
      </div>

      <div className="mt-4" data-id="element-133" data-mp="2le1ifk2wh5u1">
        <div className="flex items-baseline justify-between text-[13px]" data-id="element-134" data-mp="2lebdhm2sy36l">
          <span className="text-ink-muted" data-id="element-135" data-mp="2vhqes1024fnx">
            CO₂ <span className="tabular-nums text-ink-soft" data-id="element-136" data-mp="2vihyadgdqmxu">{room.co2} ppm</span>
          </span>
          <span className={BAND_TONE[band]} data-id="element-137" data-mp="2qk6mhx6iy2cz">{CO2_ADVICE[band]}</span>
        </div>
        <div className="relative mt-2 flex h-1.5 gap-0.5" role="meter" aria-valuemin={400} aria-valuemax={CO2_MAX} aria-valuenow={room.co2} aria-label={`CO₂ ${room.name}, ${band}`} data-id="element-138" data-mp="34ajzwf26q99p">
          <span className="h-full rounded-l-full bg-white/[0.14]" style={{
          width: `${800 / CO2_MAX * 100}%`
        }} data-id="element-139" data-mp="3e7ybo1d5y9vo" />
          <span className="h-full bg-amber/30" style={{
          width: `${400 / CO2_MAX * 100}%`
        }} data-id="element-140" data-mp="39a6ccma6vlhg" />
          <span className="h-full flex-1 rounded-r-full bg-alarm/30" data-id="element-141" data-mp="3n9fqlgeu45is" />
          <span aria-hidden className="absolute -top-1 h-3.5 w-1 -translate-x-1/2 rounded-full bg-ink" style={{
          left: `${Math.min(100, room.co2 / CO2_MAX * 100)}%`
        }} data-id="element-142" data-mp="3j7kmy59flg1u" />
        </div>
        <div className="mt-1 flex text-[11px] tabular-nums text-ink-muted" aria-hidden data-id="element-143" data-mp="1oub9619ur8ds">
          <span style={{
          width: `${800 / CO2_MAX * 100}%`
        }} data-id="element-144" data-mp="1qb8g01ofinrl">goed</span>
          <span style={{
          width: `${400 / CO2_MAX * 100}%`
        }} data-id="element-145" data-mp="1v9ryfs3tdhf7">matig</span>
          <span data-id="element-146" data-mp="1vbwe17b2s4vb">slecht</span>
        </div>
      </div>

      {/* Light + scenes */}
      <div className="mt-5 border-t border-white/[0.06] pt-4" data-id="element-147" data-mp="1zbwpqk6si1yo">
        <label className="flex items-center gap-3 text-[13px] text-ink-muted" data-id="element-148" data-mp="24azue3iuy664">
          <LightbulbIcon className={`h-4 w-4 shrink-0 ${light > 0 ? 'text-ink' : ''}`} aria-hidden data-id="element-149" data-mp="17kh7f2c9n1e9" />
          <span className="sr-only" data-id="element-150" data-mp="1blj87nltzlic">Licht {room.name}</span>
          <input type="range" min={0} max={100} step={5} value={light} onChange={e => setLight(Number(e.target.value))} onPointerUp={commitLight} onKeyUp={commitLight} onBlur={commitLight} className="h-1.5 flex-1 cursor-pointer accent-[rgb(var(--accent))]" data-id="element-151" data-mp="1bm2gsdplcxjp" />
          <span className="w-10 text-right tabular-nums text-ink-soft" data-id="element-152" data-mp="stqgrxmeu8cq">{light}%</span>
        </label>
        {extra && <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label={`Scènes ${room.name}`} data-id="element-153" data-mp="l9pi1bawh85u">
            {extra.scenes.map(s => {
          const active = scene === s;
          return <button key={s} type="button" aria-pressed={active} onClick={() => onScene(active ? null : s)} className={`whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${active ? 'bg-accent/15 text-accent' : 'bg-white/[0.05] text-ink-soft hover:bg-white/[0.09] hover:text-ink'}`} data-id="element-154" data-mp="7c11gfnv8n6l">
                  {s}
                </button>;
        })}
          </div>}
      </div>

      {/* Voice satellite */}
      <p className="mt-auto flex items-center gap-2 pt-4 text-[12px] text-ink-muted" data-id="element-155" data-mp="31556duao7v0w">
        {sat ? <>
            {sat.status === 'gedempt' || sat.status === 'offline' ? <MicOffIcon className="h-3.5 w-3.5" aria-hidden data-id="element-156" data-mp="354e2h5t6o4zm" /> : <MicIcon className="h-3.5 w-3.5" aria-hidden data-id="element-157" data-mp="2r8shratzg7d0" />}
            <span className="truncate" data-id="element-158" data-mp="2w6tyvq7ulwlu">{sat.name}</span>
            <span className={SATELLITE[sat.status].tone} data-id="element-159" data-mp="3jvvafmbz9nvc">· {SATELLITE[sat.status].label}</span>
            <span className="ml-auto whitespace-nowrap tabular-nums" data-id="element-160" data-mp="3ouq11ag6sgq0">laatst {sat.lastHeard}</span>
          </> : <>
            <MicOffIcon className="h-3.5 w-3.5" aria-hidden data-id="element-161" data-mp="3a4scuui9br0t" /> Geen spraaksatelliet
          </>}
      </p>
    </article>;
}